import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Typography, Paper, Divider, Button } from "@mui/material";
import { CopyAll } from "@mui/icons-material";

const BASE_URL = "https://geo.fusiontradebd.com/server"; // your API base

export default function GEO() {
  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upazilas, setUpazilas] = useState([]);
  const [unions, setUnions] = useState([]);
  const [wards, setWards] = useState([]);
  const [postOffices, setPostOffices] = useState([]);

  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedUpazila, setSelectedUpazila] = useState(null);
  const [selectedUnion, setSelectedUnion] = useState(null);
  const [selectedWard, setSelectedWard] = useState(null);
  const [selectedPostOffice, setSelectedPostOffice] = useState(null);

  // --- Load Divisions ---
  useEffect(() => {
    axios
      .get(`${BASE_URL}/division-list/`)
      .then((res) => setDivisions(res.data));
  }, []);

  // --- When Division changes ---
  useEffect(() => {
    if (selectedDivision) {
      axios
        .get(`${BASE_URL}/district-list/?division_id=${selectedDivision}`)
        .then((res) => setDistricts(res.data));
    } else {
      setDistricts([]);
      setUpazilas([]);
      setUnions([]);
      setPostOffices([]);
    }
    setSelectedDistrict(null);
  }, [selectedDivision]);

  // --- When District changes ---
  useEffect(() => {
    if (selectedDistrict) {
      axios
        .get(`${BASE_URL}/upazila-list/?district_id=${selectedDistrict}`)
        .then((res) => setUpazilas(res.data));
    } else {
      setUpazilas([]);
      setUnions([]);
      setPostOffices([]);
    }
    setSelectedUpazila(null);
  }, [selectedDistrict]);

  // --- When Upazila changes ---
  useEffect(() => {
    // reset lower data
    setSelectedUnion(null);
    setSelectedWard(null);
    setSelectedPostOffice(null);

    setWards([]);
    setPostOffices([]);

    if (!selectedUpazila) {
      setUnions([]);
      return;
    }

    axios
      .get(`${BASE_URL}/union-list/?upazilla_id=${selectedUpazila}`)
      .then((res) => {
        setUnions(res.data);
      })
      .catch((err) => {
        console.error(err);
        setUnions([]);
      });
  }, [selectedUpazila]);

  // --- When Union changes ---
  useEffect(() => {
    const toBanglaNumber = (num) => {
      const banglaDigits = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];

      return num
        .toString()
        .split("")
        .map((d) => banglaDigits[d])
        .join("");
    };

    setSelectedWard(null);
    setSelectedPostOffice(null);

    // no union selected
    if (!selectedUnion) {
      setWards([]);

      // ✅ still fetch post office by upazila
      if (selectedUpazila) {
        axios
          .get(`${BASE_URL}/post-office-list/?upazilla_id=${selectedUpazila}`)
          .then((res) => {
            setPostOffices(res.data);
          })
          .catch((err) => {
            console.error(err);
            setPostOffices([]);
          });
      } else {
        setPostOffices([]);
      }

      return;
    }

    const unionObj = unions.find((u) => u.id === selectedUnion);

    if (!unionObj) return;

    // detect zone
    const isZone =
      /^zone-/i.test(unionObj.name || "") ||
      /^জোন-/.test(unionObj.bn_name || "");

    // =========================
    // WARD LOGIC
    // =========================

    if (isZone) {
      // fetch real ward
      axios
        .get(`${BASE_URL}/ward-list/?union_id=${selectedUnion}`)
        .then((res) => {
          setWards(res.data);
        })
        .catch((err) => {
          console.error(err);
          setWards([]);
        });
    } else {
      // generate ward 1-9
      const wardList = Array.from({ length: 9 }, (_, i) => {
        const wardNumber = i + 1;

        return {
          id: wardNumber,
          name: `Ward ${wardNumber}`,
          bn_name: `ওয়ার্ড ${toBanglaNumber(wardNumber)}`,
        };
      });

      setWards(wardList);
    }

    // =========================
    // POST OFFICE LOGIC
    // =========================

    const postOfficeURL = isZone
      ? `${BASE_URL}/post-office-list/?union_id=${selectedUnion}`
      : `${BASE_URL}/post-office-list/?upazilla_id=${selectedUpazila}`;

    axios
      .get(postOfficeURL)
      .then((res) => {
        setPostOffices(res.data);
      })
      .catch((err) => {
        console.error(err);
        setPostOffices([]);
      });
  }, [selectedUnion, unions, selectedUpazila]);

  // --- Helper to display English + Bengali name ---
  const formatName = (item) => {
    if (!item) return "";
    if (item.name && item.bn_name) return `${item.name} (${item.bn_name})`;
    if (item.post_office_name && item.post_office_bn_name)
      return `${item.post_office_name} (${item.post_office_bn_name})`;
    return item.name || item.post_office_name || "";
  };

  // ✅ NEW: Show post office name + post code in the list box
  const formatPostOfficeDisplay = (po) => {
    if (!po) return "";
    return `${po.post_office_name} (${po.post_office_bn_name}) — ${po.post_code}`;
  };

  // --- Generic List Box ---
  const renderListBox = (
    title,
    items,
    selected,
    setSelected,
    disabled = false,
  ) => (
    <Paper
      elevation={3}
      sx={{
        flex: 1,
        minWidth: 220,
        maxHeight: 400,
        display: "flex",
        flexDirection: "column",
        p: 0, // remove inner padding, handle separately
        backgroundColor: disabled ? "#f0f0f0" : "#fff",
      }}
    >
      {/* Fixed Header (not scrolling) */}
      <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            color: "#1976d2",
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* Scrollable List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          pt: 1,
        }}
      >
        {items.length === 0 ? (
          <Typography
            variant="body2"
            align="center"
            sx={{ color: "#999", mt: 1 }}
          >
            —
          </Typography>
        ) : (
          items.map((item) => {
            const displayName =
              title === "Post Office"
                ? formatPostOfficeDisplay(item)
                : formatName(item);
            return (
              <Button
                key={item.id}
                variant={selected === item.id ? "contained" : "text"}
                color="primary"
                fullWidth
                size="small"
                sx={{
                  justifyContent: "flex-start", // already correct
                  textAlign: "left", // <-- add this
                  whiteSpace: "normal", // <-- allow wrapping
                  mb: 0.5,
                  textTransform: "none",
                  backgroundColor:
                    selected === item.id ? "#1976d2" : "transparent",
                  color: selected === item.id ? "#fff" : "#000",
                  "&:hover": {
                    backgroundColor:
                      selected === item.id ? "#1565c0" : "rgba(0,0,0,0.05)",
                  },
                }}
                onClick={() => !disabled && setSelected(item.id)}
              >
                {displayName}
              </Button>
            );
          })
        )}
      </Box>
    </Paper>
  );

  // --- Find selected items ---
  const selectedDivisionObj = divisions.find((d) => d.id === selectedDivision);
  const selectedDistrictObj = districts.find((d) => d.id === selectedDistrict);
  const selectedUpazilaObj = upazilas.find((u) => u.id === selectedUpazila);
  const selectedUnionObj = unions.find((u) => u.id === selectedUnion);
  const selectedWardObj = wards.find((w) => w.id === selectedWard);
  const selectedPostOfficeObj = postOffices.find(
    (p) => p.id === selectedPostOffice,
  );

  // --- Copy text ---
  const handleCopy = () => {
    const textToCopy = `
      Division: ${formatName(selectedDivisionObj) || "—"}
      District: ${formatName(selectedDistrictObj) || "—"}
      Upazila/Thana/City Corporation: ${formatName(selectedUpazilaObj) || "—"}
      Pouroseba/Union/Zone: ${formatName(selectedUnionObj) || "—"}
      Ward: ${formatName(selectedWardObj) || "—"}
      Police Station: ${
        selectedPostOfficeObj
          ? `${selectedPostOfficeObj.police_station_name} (${selectedPostOfficeObj.police_station_bn_name})`
          : "—"
      }
      Post Office: ${formatName(selectedPostOfficeObj) || "—"}
      Post Code: ${selectedPostOfficeObj ? selectedPostOfficeObj.post_code : "—"}
    `;
    navigator.clipboard.writeText(textToCopy);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 5,
      }}
    >
      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: "bold",
          color: "#1976d2",
          mb: 4,
          textAlign: "center",
          letterSpacing: 1.5,
        }}
      >
        EKATTOR GEO BD
      </Typography>

      {/* 5 Column Row */}
      <Box
        sx={{
          width: "95%",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
        }}
      >
        {renderListBox(
          "Division",
          divisions,
          selectedDivision,
          setSelectedDivision,
        )}
        {renderListBox(
          "District",
          districts,
          selectedDistrict,
          setSelectedDistrict,
          !selectedDivision,
        )}
        {renderListBox(
          "Upazila / Thana / City Corporation",
          upazilas,
          selectedUpazila,
          setSelectedUpazila,
          !selectedDistrict,
        )}
        {renderListBox(
          "Pouroseba / Union / Zone",
          unions,
          selectedUnion,
          setSelectedUnion,
          !selectedUpazila,
        )}
        {renderListBox(
          "Ward",
          wards,
          selectedWard,
          setSelectedWard,
          !selectedUnion,
        )}
        {renderListBox(
          "Post Office",
          postOffices,
          selectedPostOffice,
          setSelectedPostOffice,
          !selectedUpazila,
        )}
      </Box>

      {/* Info Box */}
      <Paper
        elevation={2}
        sx={{
          mt: 4,
          width: "90%",
          maxWidth: 700,
          p: 3,
          textAlign: "left",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: "#1976d2" }}>
            Selected Location Details
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CopyAll />}
            onClick={handleCopy}
          >
            Copy
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Typography>
          <strong>Division:</strong> {formatName(selectedDivisionObj) || "—"}
        </Typography>
        <Typography>
          <strong>District:</strong> {formatName(selectedDistrictObj) || "—"}
        </Typography>
        <Typography>
          <strong>Upazila/Thana/City Corporation:</strong>{" "}
          {formatName(selectedUpazilaObj) || "—"}
        </Typography>
        <Typography>
          <strong>Pouroseba/Union/Zone:</strong>{" "}
          {formatName(selectedUnionObj) || "—"}
        </Typography>
        <Typography>
          <strong>Ward:</strong> {formatName(selectedWardObj) || "—"}
        </Typography>
        <Typography>
          <strong>Police Station:</strong>{" "}
          {selectedPostOfficeObj
            ? `${selectedPostOfficeObj.police_station_name} (${selectedPostOfficeObj.police_station_bn_name})`
            : "—"}
        </Typography>
        <Typography>
          <strong>Post Office:</strong>{" "}
          {formatName(selectedPostOfficeObj) || "—"}
        </Typography>
        <Typography>
          <strong>Post Code:</strong>{" "}
          {selectedPostOfficeObj ? selectedPostOfficeObj.post_code : "—"}
        </Typography>
      </Paper>
    </Box>
  );
}
